import React, { useState, useEffect } from 'react';
import { RiDeleteBinLine, RiEdit2Line } from "react-icons/ri";
import { IoMdCloseCircleOutline, IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FaRegSave } from "react-icons/fa";

const ParticipantItem = ({
    participant,
    isEditingThis,
    isVerifyingThis,
    onEditRequest, // Wird von PollPage aufgerufen, wenn der User auf "Bearbeiten" klickt. PollPage implementiert die Logik, um /verify-email (ggf. ohne E-Mail-Body) aufzurufen.
    onCancelVerification, 
    onConfirmVerification, // Wird aufgerufen, wenn der User im Verifizierungsformular eine E-Mail eingibt und bestätigt. PollPage ruft dann /verify-email MIT E-Mail-Body auf.
    onSaveEdit, 
    onCancelEdit, 
    onDeleteParticipant, 
    globalLoading,
    isLast
}) => {
    const [editFormData, setEditFormData] = useState({});
    const [verificationEmailInput, setVerificationEmailInput] = useState('');
    const [itemEditError, setItemEditError] = useState(null);
    const [itemVerificationError, setItemVerificationError] = useState(null);
    const [itemActionLoading, setItemActionLoading] = useState(false);

    useEffect(() => {
        if (isEditingThis) {
            setEditFormData({
                firstname: participant.firstname || '', 
                lastname: participant.lastname || '',
                guests: participant.guests || 0
            });
            setItemEditError(null);
            setVerificationEmailInput('');
            setItemVerificationError(null);
        }
        if (isVerifyingThis) {
            setVerificationEmailInput('');
            setItemVerificationError(null);
            setItemEditError(null);
        }
    }, [isEditingThis, isVerifyingThis, participant]);

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
        setItemEditError(null);
    };

    const handleVerificationEmailInputChange = (e) => {
        setVerificationEmailInput(e.target.value);
        setItemVerificationError(null);
    };

    const localHandleConfirmVerification = async () => {
        if (!verificationEmailInput || !verificationEmailInput.includes('@')) {
            setItemVerificationError('Bitte gib eine gültige Email-Adresse ein.');
            return;
        }
        setItemActionLoading(true);
        setItemVerificationError(null);
        try {
            // Delegiert an PollPage.jsx, welche den API-Call an /verify-email mit der E-Mail macht.
            // PollPage.jsx wird dann basierend auf der Backend-Antwort isEditingThis und isVerifyingThis setzen.
            await onConfirmVerification(participant._id, verificationEmailInput);
        } catch (e) {
            setItemVerificationError(e.response?.data?.message || e.message || "Verifizierung fehlgeschlagen.");
        } finally {
            setItemActionLoading(false);
        }
    };

    const localHandleSave = async () => {
        if (!editFormData.firstname || !editFormData.lastname) {
            setItemEditError("Vorname und Nachname sind erforderlich.");
            return;
        }
        const guestCount = parseInt(editFormData.guests, 10);
        if (isNaN(guestCount) || guestCount < 0) {
            setItemEditError("Gästezahl muss eine positive Zahl oder null sein.");
            return;
        }
        setItemActionLoading(true);
        setItemEditError(null);
        try {
            // Delegiert an PollPage.jsx, welche den PATCH-Request sendet.
            // Das Backend prüft den Auth-Cookie.
            await onSaveEdit(participant._id, { firstname: editFormData.firstname, lastname: editFormData.lastname, guests: guestCount, participation: participant.participation });
        } catch (err) {
            setItemEditError(err.response?.data?.message || err.message || "Fehler beim Speichern.");
        } finally {
            setItemActionLoading(false);
        }
    };

    const handleDeleteInEditMode = async () => {
        if (window.confirm('Bist du sicher, dass du diese Teilnahme löschen möchtest?')) {
            setItemActionLoading(true);
            try {
                // Delegiert an PollPage.jsx, welche den DELETE-Request sendet.
                // Das Backend prüft den Auth-Cookie.
                await onDeleteParticipant(participant._id);
            } catch (err) {
                console.error("Fehler beim Löschen im Edit-Modus:", err);
                setItemEditError(err.response?.data?.message || err.message || "Löschen fehlgeschlagen.");
            } finally {
                setItemActionLoading(false);
            }
        }
    };

    const isDisabled = globalLoading || itemActionLoading;
    const rowClasses = `row bg-light align-items-center py-2 border-top mx-0 ${isLast ? 'border-bottom' : ''}`;
    const editVerificationRowClasses = `bg-light-subtle align-items-center py-2 px-2 border rounded mx-0 my-1 shadow-sm`;

    if (isVerifyingThis) {
        return (
            <div className={editVerificationRowClasses}>                       
                <div className='text-end'>
                    <button type="button" className="btn-close" onClick={onCancelVerification} title="Abbrechen" disabled={isDisabled}></button>
                </div>
                {itemVerificationError && <div className="alert alert-danger fs-7 p-1 mt-1 mb-1">{itemVerificationError}</div>}
                <label htmlFor={`verifyEmail-${participant._id}`} className='fs-7 text-muted fw-semibold ps-1'>Deine Email-Adresse zur Verifizierung:</label>
                <input
                    id={`verifyEmail-${participant._id}`}
                    type="email"
                    className="form-control form-control-sm mt-1"
                    value={verificationEmailInput}
                    onChange={handleVerificationEmailInputChange}
                    placeholder="max.mustermann@example.com"
                    aria-label="Email zur Verifizierung"
                    disabled={isDisabled}
                    onKeyDown={(event) => { if (event.key === 'Enter') localHandleConfirmVerification();}}
                />
                <div className="col-12 pt-3">
                    <div className="d-flex justify-content-center">
                        <button 
                            type="button" 
                            className="btn btn-sm btn-danger d-flex align-items-center rakete-font"
                            onClick={localHandleConfirmVerification} 
                            disabled={isDisabled} 
                            title="Verifizieren"
                        >
                            <IoMdCheckmarkCircleOutline size={20} className='me-1' /> Verifizieren
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isEditingThis) {
        return (
            <>
                <div className={editVerificationRowClasses}>
                    <div className='text-end mx-2 mt-1'>
                        <button type="button" className="btn-close" onClick={onCancelEdit} title="Bearbeitung abbrechen" disabled={isDisabled}></button>
                    </div>
                    <div className='row justify-content-center gx-2'>
                        {itemEditError && <div className="col-12 alert alert-danger fs-8 rakete-font p-1 mb-2">{itemEditError}</div>}
                        <div className="col-12 col-md-5 mb-2 mb-md-0">
                            <label htmlFor={`firstname-${participant._id}`} className="form-label fs-7 text-muted fw-semibold">Vorname</label>
                            <input id={`firstname-${participant._id}`} type="text" className="form-control form-control-sm" name="firstname" value={editFormData.firstname} onChange={handleEditFormChange} placeholder="Vorname" aria-label="Vorname" disabled={isDisabled} />
                        </div>
                        <div className="col-12 col-md-5 mb-2 mb-md-0">
                            <label htmlFor={`lastname-${participant._id}`} className="form-label fs-7 text-muted fw-semibold">Nachname</label>
                            <input id={`lastname-${participant._id}`} type="text" className="form-control form-control-sm" name="lastname" value={editFormData.lastname} onChange={handleEditFormChange} placeholder="Nachname" aria-label="Nachname" disabled={isDisabled} />
                        </div>
                        <div className="col-12 col-md-2 mb-2 mb-md-0">
                            <label htmlFor={`guests-${participant._id}`} className="form-label fs-7 text-muted fw-semibold">Gäste</label>
                            <input id={`guests-${participant._id}`} type="number" className="form-control form-control-sm" name="guests" value={editFormData.guests} onChange={handleEditFormChange} min="0" aria-label="Anzahl Gäste" disabled={isDisabled} />
                        </div>
                    </div>
                    <div className="col-12 mt-3">
                        <div className="d-flex justify-content-center gap-2 align-items-center">
                             <button 
                                type="button" 
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center rakete-font fs-7" 
                                onClick={handleDeleteInEditMode} 
                                title="Teilnahme löschen" 
                                disabled={isDisabled}
                            >
                                <RiDeleteBinLine size={18} className='me-1' /> Löschen
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-sm btn-danger d-flex align-items-center rakete-font fs-7" 
                                onClick={localHandleSave} 
                                title="Änderungen speichern" 
                                disabled={isDisabled}
                            >
                                <FaRegSave size={16} className='me-1' /> Speichern
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Standardansicht
    return (
        <div className={rowClasses}>
            <div className="col-9 fs-7 text-start fw-semibold">
                {participant.firstname} {participant.lastname}
                <span className='ms-2 fw-semibold rakete-font fs-7 rakete-rot'>
                    {participant.guests > 0 ? `+${participant.guests}` : ''}
                </span>
            </div>
            <div className="col-3 text-end">
                <RiEdit2Line
                    size={20}
                    className='hover icon-edit mx-1 my-1'
                    onClick={() => onEditRequest(participant)} // Ruft handleEditRequest in PollPage auf
                    title="Teilnahme bearbeiten/verwalten"
                    style={{ cursor: 'pointer' }}
                    disabled={isDisabled}
                />
            </div>
        </div>
    );
};

export default ParticipantItem;
